import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  RiderCheckboxContainerStyled,
  RiderDrawerLabelContainer,
  RiderItemStyled,
  RiderLabelOnlyStyled,
  RiderPPPContainerStyled,
} from './RiderBenefitsStyled';
import { useBenefitPlansQuery } from 'enrollment/queries';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgLifeEventId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useConfigurationQuery } from 'theSource/queries';
import { PsCheckboxTuner, PsDrawerDialog } from 'stencilSetup';
import { PageContent } from '../PageContentDetails/PageContentWrapper';
import { usePageContentQuery } from 'enrollment/queries/usePageContentQuery';
import { PlanRiderDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  hasContentBlocks,
  hasRequiredPageContent,
} from '../PageContentDetails/utils';
import { Spacer } from 'theSource/components';
import { RiderCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import { isRiderLERestricted } from '.';
import { getPremiumStyled, RETURN_DISPLAY } from 'enrollment/utils';
import { selectPerPayPeriodText } from 'enrollment/services';

interface RiderBenefitItemProps {
  riderInfo: PlanRiderDto;
  riderCoverage?: RiderCoverageDto;
  orgPlanYearId: number;
  electedRiders?: number[];
  setElectedRiders?: Dispatch<SetStateAction<number[]>>;
  costMap?: Map<number, number>;
  setCostMap?: Dispatch<SetStateAction<Map<number, number>>>;
  displayCheckbox?: boolean;
  resetElectedRiders?: boolean;
  setResetElectedRiders?: Dispatch<SetStateAction<boolean>>;
  ridersForResetting?: number[];
  disabled?: boolean;
  coverageLineId?: number;
}

export const RiderBenefitItem = ({
  riderInfo,
  riderCoverage,
  orgPlanYearId,
  electedRiders,
  setElectedRiders,
  costMap = new Map<number, number>(),
  setCostMap = () => {
    return new Map<number, number>();
  },
  displayCheckbox = false,
  resetElectedRiders,
  setResetElectedRiders,
  ridersForResetting,
  disabled = false,
  coverageLineId,
}: RiderBenefitItemProps) => {
  const { t } = useTranslation('common');
  const contextLookup = useSelector(selectEnrollmentContext);
  const orgLifeEventID = useSelector(selectEnrollmentOrgLifeEventId);
  const perPayText = useSelector(selectPerPayPeriodText);
  const isMobile = window.innerWidth < 744;
  const [showCheckbox, setShowCheckbox] = useState(displayCheckbox);
  const [checked, setChecked] = useState<boolean>(Boolean(riderCoverage));

  const { configuration } = useConfigurationQuery();
  const { plansData, isLoadingPlans, isFetchingPlans } = useBenefitPlansQuery({
    orgBenefitId: riderInfo.org_benefit_id,
    enrollmentContextType: contextLookup,
    dependentIds: [],
    enabled:
      riderInfo.elect_with_plan && (Boolean(configuration) || !riderCoverage),
    lumosApiHost: configuration?.apiHost,
    keepPreviousData: true,
    includeContentDetails: true,
  });
  const { data: restrictions, isLoading: isLoadingRestrictions } =
    useRestrictionsQuery({
      orgLifeEventID: orgLifeEventID,
      orgPlanYearID: orgPlanYearId,
      enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
    });

  const riderId = riderInfo.rider_benefit_id;
  const plan = plansData?.data.plans[0];
  const planCost =
    Number(riderCoverage?.req_subscriber_premium) ||
    Number(plan?.subscriber_premium);
  const planId = String(riderCoverage?.org_plan_id);

  const { pageContent } = usePageContentQuery({
    pageContentLookupCode: 'ENROLL_COVERAGE',
    orgPlanYearId: orgPlanYearId,
    benefitLookupCode: riderInfo.benefit_lookup_code,
    orgPlanIds: planId,
    enabled: riderInfo.elect_with_plan && Boolean(plansData),
  });

  const showPageContent =
    hasContentBlocks(pageContent) || hasRequiredPageContent(pageContent);

  const handleClick = (e: any) => {
    if (e.target.tagName !== 'INPUT') {
      return;
    }

    // update for total cost banner
    if (costMap.get(riderId) === undefined) {
      costMap.set(riderId, planCost);
    } else {
      costMap.delete(riderId);
    }
    setCostMap(costMap);

    // update for election endpoint
    if (setElectedRiders && electedRiders) {
      setElectedRiders(() => {
        if (electedRiders.includes(riderId)) {
          return electedRiders.filter((id) => id !== riderId);
        }
        return [...electedRiders, riderId];
      });
    }
  };

  const isLoading = isLoadingPlans || isFetchingPlans || isLoadingRestrictions;

  useEffect(() => {
    if (resetElectedRiders && setResetElectedRiders) {
      setShowCheckbox(false);
      setChecked(Boolean(ridersForResetting?.includes(riderId)));
      // reloads the checkbox in the backgroung if the user resets the elected riders. Not visible time to the user
      setTimeout(() => {
        if (displayCheckbox) {
          setShowCheckbox(true);
        }
      }, 500);
      setResetElectedRiders(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetElectedRiders, displayCheckbox, setResetElectedRiders]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        height={50}
        role="status"
        aria-label="Loading..."
      />
    );
  }

  if (!plan) {
    return null;
  }

  return (
    <RiderItemStyled className="rider-line" data-testid="rider-item-container">
      <RiderCheckboxContainerStyled>
        {showCheckbox ? (
          <PsCheckboxTuner
            data-testid={`rider_select${riderId}`}
            inputId={`rider_select${riderId}${coverageLineId ? '-' + coverageLineId : ''}`}
            name={`rider_select${riderId}`}
            label={riderInfo.benefit_name}
            labelClass="rider-checkbox-label-text"
            checked={checked}
            onClick={handleClick}
            disabled={
              disabled || isRiderLERestricted(restrictions, riderInfo, checked)
            }
          />
        ) : (
          <RiderLabelOnlyStyled>
            <p>{riderInfo.benefit_name}</p>
          </RiderLabelOnlyStyled>
        )}
        {showPageContent ? (
          <RiderDrawerLabelContainer>
            <PsDrawerDialog
              dialogId={`page_content_drawer_${riderId}`}
              dialogTriggerLabel={t('enrollment.viewRiderDetails')}
              dialogTriggerClass="content-link mb-xl-5"
              dialogLarge
              dialogTriggerLink
            >
              <div>
                <PageContent
                  pageContent={pageContent}
                  translatedTitle={t('enrollment.pageContentModalTitle', {
                    benefit_name: riderInfo.benefit_name,
                  })}
                />
              </div>
            </PsDrawerDialog>
          </RiderDrawerLabelContainer>
        ) : (
          !isMobile && <Spacer height={8} />
        )}
      </RiderCheckboxContainerStyled>
      <RiderPPPContainerStyled>
        {planCost > 0 &&
          getPremiumStyled({
            premium: String(planCost),
            valueOnly: RETURN_DISPLAY,
            perPayText: perPayText,
          })}
      </RiderPPPContainerStyled>
    </RiderItemStyled>
  );
};
